import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";

import { SignalCard, type SignalCardData } from "@/components/signal-card";
import { liveSignalFor, PAIRS } from "@/lib/mock-signals";
import { Card } from "@/components/ui/card";
import { useLivePrices } from "@/hooks/use-live-prices";
import { useConfluenceSignal } from "@/hooks/use-confluence-signal";
import { hasApiKey } from "@/lib/twelve-data";
import { AlertTriangle, Lock, Sparkles } from "lucide-react";
import { useEffectivePlan, isPairUnlocked } from "@/hooks/use-my-profile";
import { AgentCouncilPanel } from "@/components/AgentCouncil";
import type { CouncilVerdict } from "@/lib/agents/agentCouncil";
import { ReflexivityGauge } from "@/components/ReflexivityGauge";
import { LiveDataStatus } from "@/components/LiveDataStatus";
import { SignalQuotaBadge } from "@/components/signal-quota-badge";
import { getMyQuota } from "@/lib/rate-limit.functions";
import { MarketIntelligencePanel } from "@/components/market-intelligence-panel";

export const Route = createFileRoute("/_authenticated/dashboard")({
  component: Dashboard,
});

function formatLivePrice(pair: string, n: number): string {
  if (pair === "BTCUSD" || pair === "ETHUSD") return n.toFixed(0);
  if (pair === "SPX500" || pair === "NAS100" || pair === "US30") return n.toFixed(1);
  if (pair === "WTIUSD") return n.toFixed(2);
  if (pair === "XAGUSD") return n.toFixed(3);
  if (pair === "XAUUSD") return n.toFixed(2);
  if (pair.endsWith("JPY")) return n.toFixed(3);
  return n.toFixed(5);
}

function tradingViewSymbol(pair: string): string {
  if (pair === "BTCUSD") return "BITSTAMP:BTCUSD";
  if (pair === "ETHUSD") return "BITSTAMP:ETHUSD";
  if (pair === "SPX500") return "TVC:SPX";
  if (pair === "NAS100") return "TVC:NDX";
  if (pair === "US30") return "TVC:DJI";
  if (pair === "WTIUSD") return "TVC:USOIL";
  if (pair === "XAGUSD") return "OANDA:XAGUSD";
  if (pair === "XAUUSD") return "OANDA:XAUUSD";
  return `FX:${pair}`;
}

function Dashboard() {
  const [pair, setPair] = useState<string>("EURUSD");
  const keyPresent = hasApiKey();
  const pairsList = useMemo(() => PAIRS.slice(), []);
  const { prices, loading: pricesLoading, usingCache: pricesCached } = useLivePrices(pairsList, pair);
  const plan = useEffectivePlan();
  const isFree = plan === "free";
  const livePrice = prices[pair]?.price;

  // Quota gate — server-enforced via assertWithinQuota; we mirror it here for UX
  const fetchQuota = useServerFn(getMyQuota);
  const { data: quotaData } = useQuery({
    queryKey: ["my-quota", "signals"],
    queryFn: () => fetchQuota(),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const quotaBlocked = Boolean(
    quotaData && !quotaData.signals.isAdmin && quotaData.signals.remaining <= 0,
  );

  const signalState = useConfluenceSignal(pair, livePrice, !quotaBlocked);
  const [verdict, setVerdict] = useState<CouncilVerdict | null>(null);

  // If a free user lands on a locked pair, snap back to EURUSD
  useEffect(() => {
    if (!isPairUnlocked(pair, plan)) setPair("EURUSD");
  }, [pair, plan]);

  // P0 stale-data guard: clear council verdict immediately on pair switch so
  // the right panel never renders previous-pair text under the new label.
  useEffect(() => {
    setVerdict(null);
  }, [pair]);


  // Fallback to deterministic mock data when there's no API key
  const fallback = useMemo(() => liveSignalFor(pair, 0), [pair]);
  const card: SignalCardData | null = signalState.result
    ? {
        pair,
        direction: signalState.result.direction,
        score: signalState.result.score,
        entry: signalState.result.entry,
        sl: signalState.result.sl,
        tp: signalState.result.tp,
        rr: signalState.result.rr,
        ageMins: signalState.generatedAt
          ? Math.max(0, Math.floor((Date.now() - signalState.generatedAt) / 60_000))
          : 0,
        breakdown: signalState.result.breakdown,
      }
    : !keyPresent
      ? {
          pair,
          direction: fallback.direction,
          score: fallback.score,
          entry: fallback.entry,
          sl: fallback.sl,
          tp: fallback.tp,
          rr: fallback.rr,
          ageMins: fallback.ageMins,
          breakdown: fallback.breakdown,
        }
      : null;

  return (
    <div className="flex min-w-0 flex-1 overflow-x-hidden">
      <main className="w-full min-w-0 flex-1 overflow-x-hidden p-3 sm:p-4 lg:p-6">

        <div className="mb-3 flex min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div className="w-full min-w-0 sm:w-auto sm:max-w-full">
            <LiveDataStatus />
          </div>
          <div className="self-end sm:self-auto">
            <SignalQuotaBadge />
          </div>
        </div>
        {isFree && (
          <div className="mb-3 flex flex-col gap-2 rounded-md border border-primary/40 bg-primary/10 px-3 py-2 text-xs sm:flex-row sm:items-center sm:justify-between">
            <span className="flex items-start gap-2 text-primary">
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>This intelligence is delayed. Upgrade to Analyst for real-time.</span>
            </span>
            <Link
              to="/upgrade"
              className="self-start rounded-md bg-primary px-2.5 py-1 text-[11px] font-bold text-primary-foreground sm:self-auto"
            >
              Upgrade to Analyst
            </Link>
          </div>
        )}
        {!keyPresent && (
          <div className="mb-3 flex items-center gap-2 rounded-md border border-warn/40 bg-warn/10 px-3 py-2 text-xs text-warn">
            <AlertTriangle className="h-4 w-4" />
            Running in demo mode. Add your Twelve Data API key in{" "}
            <Link to="/settings" className="underline">
              Settings
            </Link>{" "}
            to enable live prices & real AI signals.
          </div>
        )}
        {keyPresent && pricesCached && (
          <div className="mb-3 flex items-center gap-2 rounded-md border border-warn/40 bg-warn/10 px-3 py-2 text-xs text-warn">
            <AlertTriangle className="h-4 w-4" />
            Using cached data — last price fetch failed.
          </div>
        )}
        {pair === "BTCUSD" && (
          <div className="mb-3 rounded-md border border-orange-500/40 bg-orange-500/10 px-3 py-2 text-xs text-orange-300">
            <div className="flex items-center gap-2 font-bold uppercase tracking-wider">
              <span className="rounded-sm bg-orange-500/30 px-1.5 py-0.5 text-[10px]">β BETA</span>
              Crypto Mode
            </div>
            <p className="mt-1 leading-snug text-orange-200/80">
              Crypto signals use modified agent weights. Cryptocurrency markets operate 24/7
              with higher volatility. Use smaller position sizes. Past performance in forex
              markets may not reflect crypto performance. Confidence scores are capped at 70%.
            </p>
          </div>
        )}
        <div className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="min-w-0 flex flex-col gap-4">
            <Card className="min-w-0 max-w-full overflow-hidden border-border/60 bg-card/60 p-0">
              <div className="flex flex-col gap-2 border-b border-border/60 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:px-4">
                <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="font-mono text-base font-bold sm:text-lg">{`${pair.slice(0, 3)}/${pair.slice(3)}`}</span>
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground sm:text-xs">
                    1H · TradingView
                  </span>
                  {livePrice && (
                    <span className="font-mono text-xs text-primary sm:text-sm">
                      {formatLivePrice(pair, livePrice)}
                    </span>
                  )}
                </div>
                <span className="inline-flex shrink-0 items-center gap-1.5 self-end text-xs text-buy sm:self-auto">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-buy" /> Live
                </span>
              </div>
              <div className="relative w-full overflow-hidden h-[320px] sm:h-[440px] md:h-[480px] lg:h-[520px]">
                <iframe
                  key={pair}
                  title="chart"
                  src={`https://s.tradingview.com/widgetembed/?frameElementId=tv_chart&symbol=${encodeURIComponent(tradingViewSymbol(pair))}&interval=60&theme=dark&style=1&toolbarbg=0a0e1a&hide_side_toolbar=1&allow_symbol_change=0&hide_top_toolbar=0&save_image=0`}
                  className="absolute inset-0 block h-full w-full border-0"
                  allowTransparency
                  loading="lazy"
                />
              </div>
            </Card>
            <div className="grid min-w-0 gap-4 items-stretch xl:grid-cols-2">
              <div className="min-w-0 [&>*]:h-full">
                <MarketIntelligencePanel
                  activePair={pair}
                  onSelectPair={setPair}
                  prices={prices}
                  plan={plan}
                />
              </div>
              <div key={`council-${pair}`} className="min-w-0 [&>*]:h-full">
                <AgentCouncilPanel pair={pair} livePrice={livePrice} onVerdict={setVerdict} />
              </div>
            </div>
          </div>

          {quotaBlocked ? (
            <Card className="flex max-w-full flex-col items-center justify-center gap-4 overflow-hidden border-primary/40 bg-gradient-to-br from-primary/10 to-card/80 p-5 text-center backdrop-blur sm:p-6">
              <div className="rounded-full bg-primary/15 p-3 text-primary">
                <Sparkles className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-base font-bold">Daily market views limit reached</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  You've used {quotaData?.signals.used}/{quotaData?.signals.limit} market views on the{" "}
                  {quotaData?.signals.plan ?? "free"} plan. Resets in{" "}
                  {quotaData?.signals.resetInHours}h.
                </p>
              </div>
              <Link
                to="/upgrade"
                className="rounded-md bg-primary px-4 py-2 text-sm font-bold text-primary-foreground shadow-lg shadow-primary/30 transition hover:opacity-90"
              >
                Upgrade for unlimited market views
              </Link>
              <p className="text-[11px] text-muted-foreground">
                Pro · 50/day · all pairs · real-time
              </p>
            </Card>
          ) : (
            <SignalCard
              key={pair}
              signal={card}
              verdict={verdict && verdict.pair === pair ? verdict : null}
              loading={signalState.loading && keyPresent && !card}
              computing={signalState.computing}
              usingCache={signalState.usingCache}
            />
          )}
        </div>

        <div key={`reflex-${pair}`} className="mt-4">
          <ReflexivityGauge pair={pair} />
        </div>





        {/* mobile pair selector */}
        <div className="mt-4 flex gap-2 overflow-x-auto lg:hidden">
          {PAIRS.map((p) => {
            const locked = !isPairUnlocked(p, plan);
            return (
              <button
                key={p}
                onClick={() => !locked && setPair(p)}
                disabled={locked}
                className={`whitespace-nowrap rounded-md border px-3 py-1.5 font-mono text-xs ${pair === p && !locked ? "border-primary bg-primary/15 text-primary" : "border-border bg-card/40 text-muted-foreground"} ${locked ? "opacity-50" : ""}`}
              >
                {locked ? "🔒 " : ""}{`${p.slice(0, 3)}/${p.slice(3)}`}
              </button>
            );
          })}
        </div>
      </main>
    </div>
  );
}
